import { DashboardBlockPosition } from "@/dashboard/blocks/__generated__/DashboardBlockItemFragment.graphql"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import { SwiperProps } from "@components/carousel/Swiper"

interface CarouselSizeResult {
  sliceSize: number
  breakpoints?: SwiperProps["config"]["breakpoints"]
  slidesPerView?: SwiperProps["config"]["slidesPerView"]
  centeredSlides?: boolean
}

// Breakpoint configurations for different dashboard layouts
// The carousel slides are sized to align with the dashboard grid system
// fullWidth carousels span the entire grid width, while one/two column carousels
// use proportional sizes (multiple of the fullWidth) to maintain consistent card widths across the layout
const BREAKPOINTS = {
  fullWidth: {
    0: { slidesPerView: 1 },
    600: { slidesPerView: 0.9 },
    1280: { slidesPerView: 0.9 },
    1366: { slidesPerView: 1.1 },
    1536: { slidesPerView: 1.2 },
    1920: { slidesPerView: 1.4 },
  },
  // Carousel in two column layout has one less slides than that in a one column layout
  twoColumn: {
    0: { slidesPerView: 1 },
    600: { slidesPerView: 1.8 },
    1280: { slidesPerView: 1.8 },
    1366: { slidesPerView: 2.2 },
    1536: { slidesPerView: 2.4 },
    1920: { slidesPerView: 2.8 },
  },
  oneColumn: {
    0: { slidesPerView: 1 },
    600: { slidesPerView: 2.7 },
    1280: { slidesPerView: 2.7 },
    1366: { slidesPerView: 3.3 },
    1536: { slidesPerView: 3.6 },
    1920: { slidesPerView: 4.2 },
  },
} as const

/**
 * Automatically calculates the optimal DiscoCarousel `breakpoints` or `slidesPerView` prop for a dashboard
 * block, based on that block's position (side/main), whether
 * the dashboard has one or two columns, or block needs to span the entire grid width
 */
export function useDashboardBlockCarouselSize(
  block?: {
    position: DashboardBlockPosition
  },
  fullWidth?: boolean
): CarouselSizeResult {
  const { isOneColumn } = useDashboardContext()!
  const isSide = block?.position === "side"

  // Base configuration for all carousel types
  const baseConfig: CarouselSizeResult = {
    // for paginated carousels, it's safe/more efficient to load more than the max size at a time
    sliceSize: 5,
    // turns off centered slides so the carousel blocks are left aligned in the same grid
    // centererd slides also doesn't work well with carousels with even number of cards since swiper cannot find the center
    // of the carousel easily, casuing it to randomly choose a side to align on breakpoint change.
    centeredSlides: false,
  }

  // For side blocks, render 1 block only
  if (isSide) {
    return {
      ...baseConfig,
      slidesPerView: 1,
    }
  }

  // For full width blocks, use full width breakpoints
  if (fullWidth) {
    return {
      ...baseConfig,
      breakpoints: BREAKPOINTS.fullWidth,
    }
  }

  // For main blocks, use layout-specific breakpoints
  const breakpoints = isOneColumn ? BREAKPOINTS.oneColumn : BREAKPOINTS.twoColumn

  return {
    ...baseConfig,
    breakpoints,
  }
}
